import React from "react";
import TextField from "@material-ui/core/TextField";
import InputRules from "./InputRules";
import {InputAdornment} from "@material-ui/core";
import {NormalParams} from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/NormalParams";
import NormalParamsInput from "./NormalParamsInput";

interface Props {
  normalParams: NormalParams;
  normalMean: number;
  normalStd: number;
  normalMedian: number;
  normalErrorFactor: number;
  normalP5: number;
  normalP95: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, error?: boolean) => void;
}

class NormalInput extends React.Component<Props> {

  private handleMeanChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { normalStd, onChange } = this.props;
    const isMeanValid = InputRules.isMeanValid(e.target.value);
    const isStdValid = InputRules.isStdValid(normalStd);
    onChange(e, !isMeanValid || !isStdValid);
  };

  private handleStdChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { normalMean, onChange } = this.props;
    const isMeanValid = InputRules.isMeanValid(normalMean);
    const isStdValid = InputRules.isStdValid(e.target.value);
    onChange(e, !isMeanValid || !isStdValid);
  };

  private handleMedianChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { normalErrorFactor, onChange } = this.props;
    const isMedianValid = InputRules.isMedianValid(e.target.value);
    const isErrorFactorValid = InputRules.isErrorFactorValid(normalErrorFactor);
    onChange(e, !isMedianValid || !isErrorFactorValid);
  };

  private handleErrorFactorChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { normalMedian, onChange } = this.props;
    const isMedianValid = InputRules.isMedianValid(normalMedian);
    const isErrorFactorValid = InputRules.isErrorFactorValid(e.target.value);
    onChange(e, !isMedianValid || !isErrorFactorValid);
  };

  private handleP5Change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { normalP95, onChange } = this.props;
    const isP5Valid = InputRules.isP5Valid(e.target.value, normalP95);
    const isP95Valid = InputRules.isP95Valid(e.target.value, normalP95);
    onChange(e, !isP5Valid || !isP95Valid);
  };

  private handleP95Change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { normalP5, onChange } = this.props;
    const isP5Valid = InputRules.isP5Valid(normalP5, e.target.value);
    const isP95Valid = InputRules.isP95Valid(normalP5, e.target.value);
    onChange(e, !isP5Valid || !isP95Valid);
  };

  render() {

    const {
      normalMean,
      normalStd,
      normalParams,
      onChange,
      normalMedian,
      normalErrorFactor,
      normalP5,
      normalP95
    } = this.props;

    const isMeanValid = InputRules.isMeanValid(normalMean);
    const isStdValid = InputRules.isStdValid(normalStd);
    const isMedianValid = InputRules.isMedianValid(normalMedian);
    const isErrorFactorValid = InputRules.isErrorFactorValid(normalErrorFactor);
    const isP5Valid = InputRules.isP5Valid(normalP5, normalP95);
    const isP95Valid = InputRules.isP95Valid(normalP5, normalP95);

    return (
      <React.Fragment>
        <NormalParamsInput
          name="normal_params"
          onChange={onChange}
          value={normalParams}
          isMeanValid={isMeanValid}
          isStdValid={isStdValid}
          isMedianValid={isMedianValid}
          isErrorFactorValid={isErrorFactorValid}
          isP5Valid={isP5Valid}
          isP95Valid={isP95Valid}
        />
        {
          normalParams === NormalParams.MeanStd &&
            <React.Fragment>
              <TextField
                required
                label="mean"
                InputProps={{
                  startAdornment: <InputAdornment position="start">μ</InputAdornment>,
                }}
                name="normal_mean"
                value={normalMean}
                type="number"
                onChange={this.handleMeanChange}
                error={!isMeanValid}
                margin="normal"
                variant="outlined"
                fullWidth
                helperText={isMeanValid ? undefined : InputRules.DESCRIPTIONS.MEAN}
              />
              <TextField
                required
                label="std"
                InputProps={{
                  startAdornment: <InputAdornment position="start">σ</InputAdornment>,
                }}
                name="normal_std"
                value={normalStd}
                type="number"
                error={!isStdValid}
                onChange={this.handleStdChange}
                margin="normal"
                variant="outlined"
                fullWidth
                helperText={isStdValid ? undefined : InputRules.DESCRIPTIONS.STD}
              />
            </React.Fragment>
        }
        {
          normalParams === NormalParams.MedianErrorFactor &&
            <React.Fragment>
              <TextField
                required
                label="median"
                InputProps={{
                  startAdornment: <InputAdornment position="start">p50</InputAdornment>,
                }}
                name="normal_median"
                value={normalMedian}
                type="number"
                error={!isMedianValid}
                onChange={this.handleMedianChange}
                margin="normal"
                variant="outlined"
                fullWidth
                helperText={isMedianValid ? undefined : InputRules.DESCRIPTIONS.MEDIAN}
              />
              <TextField
                required
                label="error_factor"
                InputProps={{
                  startAdornment: <InputAdornment position="start">EF</InputAdornment>,
                }}
                name="normal_error_factor"
                value={normalErrorFactor}
                type="number"
                error={!isErrorFactorValid}
                onChange={this.handleErrorFactorChange}
                margin="normal"
                variant="outlined"
                fullWidth
                helperText={isErrorFactorValid ? undefined : InputRules.DESCRIPTIONS.ERROR_FACTOR}
              />
            </React.Fragment>
        }
        {
          normalParams === NormalParams.Percentiles &&
          <React.Fragment>
            <TextField
              required
              label="p5"
              InputProps={{
                startAdornment: <InputAdornment position="start">p5</InputAdornment>,
              }}
              name="normal_p5"
              value={normalP5}
              type="number"
              error={!isP5Valid}
              onChange={this.handleP5Change}
              margin="normal"
              variant="outlined"
              fullWidth
              helperText={isP5Valid ? undefined : InputRules.DESCRIPTIONS.P5}
            />
            <TextField
              required
              label="p95"
              InputProps={{
                startAdornment: <InputAdornment position="start">p95</InputAdornment>,
              }}
              name="normal_p95"
              value={normalP95}
              type="number"
              error={!isP95Valid}
              onChange={this.handleP95Change}
              margin="normal"
              variant="outlined"
              fullWidth
              helperText={isP95Valid ? undefined : InputRules.DESCRIPTIONS.P95}
            />
          </React.Fragment>
        }
      </React.Fragment>
    );
  }
}

export default NormalInput;
